import {
  LDContext,
  useFlags,
  useLDClient,
  useLDClientError,
} from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Context, ContextKey, INITIAL_CONTEXT } from ".";
import {
  awaitWithTimeout,
  LaunchDarklyFeatureFlags,
  parseFeatureFlags,
} from "./utils";
import { isEqual } from "lodash";
import { withMeticulousFlagOverrides } from "@/integrations/meticulous";

export const useLaunchDarkly = () => {
  const client = useLDClient();
  const clientError = useLDClientError();
  const flags = useFlags<LaunchDarklyFeatureFlags>();

  const [contexts, setContexts] = useState<LDContext>(INITIAL_CONTEXT);
  const [identified, setIdentified] = useState(false);
  const [identificationError, setIdentificationError] = useState<Error | null>(
    null,
  );

  useEffect(() => {
    if (!client || clientError) {
      return;
    }
    if (isEqual(contexts, INITIAL_CONTEXT)) return;

    setIdentificationError(null);
    if (isEqual(client.getContext(), contexts)) {
      setIdentified(true);
      return;
    }
    awaitWithTimeout(
      () => client.identify(contexts),
      2000,
      "LaunchDarkly identification time out",
    )
      .then(() => setIdentified(true))
      .catch((error) => setIdentificationError(error));
  }, [contexts, client, clientError]);

  const features = useMemo(
    () => withMeticulousFlagOverrides(parseFeatureFlags(flags)),
    [flags],
  );
  const setContext = useCallback(
    <K extends ContextKey, C extends Context[K]>(key: K, context: C) => {
      setContexts((current) => ({
        ...current,
        [key]: context,
      }));
    },
    [],
  );

  const clearContext = useCallback(() => {
    setContexts(INITIAL_CONTEXT);
    setIdentified(false);
  }, []);

  return {
    features,
    setContext,
    clearContext,
    identified,
    error: clientError || identificationError,
  };
};

export type FeatureFlags = ReturnType<typeof parseFeatureFlags>;
